'use client'

import {
  ArrowLeft24,
  X24,
  Home24,
  LocationPin24,
  Box24,
  ReusableBox24,
} from '@vinted/monochrome-icons'
import { Button, Cell, Dialog, Divider, Image, Navigation, Radio, Text, Icon } from '@vinted/web-ui'
import { useContext, useMemo, Fragment, ReactNode } from 'react'

import { GetShippingLabelContext } from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  GenerateLabelModal,
  PackagingTypes,
  DropOffType,
  LabelOptionsDropOffTypesModel,
  DropOffTypeKey,
  clickEvent,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'

import {
  isReturnShipment,
  isPackageTypeLabelTypeFlow,
  getReusablePackagingFlowWithDimensions,
  isTypeMailbox,
  isDropOffMapEnabled,
} from '../../utils/generate-label'

type Props = {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  onShowDropOffPointDialog?: () => void
}

const DropOffTypeSelectionModal = ({
  isOpen,
  onClose,
  onSuccess,
  onShowDropOffPointDialog,
}: Props) => {
  const { track } = useTracking()
  const dropOffTypeTranslate = useTranslate(
    'conversation.get_shipping_label.select_drop_off_method.modal',
  )
  const packagingTypeTranslate = useTranslate(
    'conversation.get_shipping_label.select_packaging_type.modal',
  )
  const a11yTranslate = useTranslate(
    'conversation.get_shipping_label.select_drop_off_method.modal.a11y.actions',
  )

  const {
    state: {
      labelTypeSelection: { clearLabelTypeSelection },
      labelOptions: { labelOptions, labelOptionsUiState, labelOptionsRewrite },
      activeModal: { setActiveModal },
      dropOffTypeSelection: {
        selectedDropOffTypeKey,
        isSubmitEnabled,
        clearDropOffTypeSelection,
        selectDropOffTypeKey,
      },
    },
  } = useContext(GetShippingLabelContext)

  const { conversation } = useConversationContext()
  const transactionId = conversation?.transaction?.id

  const labelOptionsData = useMemo(
    () => labelOptions || labelOptionsRewrite,
    [labelOptions, labelOptionsRewrite],
  )

  const { enabled: isReusablePackagingEnabled, dimensions: reusablePackageDimensions } = useMemo(
    () => getReusablePackagingFlowWithDimensions(labelOptionsRewrite),
    [labelOptionsRewrite],
  )

  const handleSelectDropOffType = (type: PackagingTypes | DropOffType | DropOffTypeKey) => () =>
    selectDropOffTypeKey(type)

  const handleTrackSubmit = () => {
    track(
      clickEvent({
        target: 'select_dropoff_type',
        screen: 'dropoff_type_selection',
        targetDetails: JSON.stringify({
          transaction_id: transactionId,
          dropoff_type_key: selectedDropOffTypeKey,
        }),
      }),
    )
  }

  const handleSubmit = () => {
    handleTrackSubmit()
    onSuccess()
  }

  const renderCarrier = () =>
    labelOptionsData?.carrier && (
      <Cell
        prefix={
          <Image
            src={labelOptionsData.carrier.iconUrl}
            size="large"
            styling="rounded"
            alt={labelOptionsData.carrier.name}
          />
        }
        title={labelOptionsData.carrier.name}
        body={!isReturnShipment(labelOptionsRewrite) && dropOffTypeTranslate('carrier_body')}
        divider
      />
    )

  const renderDropOffTypeCell = (type, icon, title, body) => (
    <Cell
      type="navigating"
      prefix={icon}
      title={title}
      body={<Text as="span" html text={body} />}
      onClick={handleSelectDropOffType(type)}
      divider
      suffix={
        <Radio
          name={`${type}-radio`}
          checked={type === selectedDropOffTypeKey}
          value={type}
          testId={`drop-off-type-selection-modal-${type}-radio`}
          aria={{
            'aria-labelledby': `drop-off-type-selection-modal-${type}-radio`,
          }}
          onChange={() => undefined}
        />
      }
    />
  )

  const renderLabelOptionsDropOffTypeMethod = (
    dropOffTypeMethod: LabelOptionsDropOffTypesModel,
  ) => (
    <Fragment key={dropOffTypeMethod.key}>
      <Cell
        type="navigating"
        prefix={
          <Image
            src={dropOffTypeMethod.dropOffTypeMeta.iconUrl}
            size="regular"
            styling="rounded"
            alt={dropOffTypeMethod.title}
          />
        }
        title={dropOffTypeMethod.title}
        body={<Text as="span" html text={dropOffTypeMethod.subtitle} />}
        onClick={handleSelectDropOffType(dropOffTypeMethod.key)}
        divider
        suffix={
          <Radio
            name={`${dropOffTypeMethod.key}-radio`}
            checked={dropOffTypeMethod.key === selectedDropOffTypeKey}
            value={dropOffTypeMethod.key}
            testId={`drop-off-type-selection-modal-${dropOffTypeMethod.key}-radio`}
            aria={{
              'aria-labelledby': `drop-off-type-selection-modal-${dropOffTypeMethod.key}-radio`,
            }}
            onChange={() => undefined}
          />
        }
      />
    </Fragment>
  )

  const renderReusableDropOffTypeMethods = () => (
    <>
      {renderDropOffTypeCell(
        PackagingTypes.MyOwnPackage,
        <Icon name={Box24} />,
        packagingTypeTranslate('my_own_packaging'),
        packagingTypeTranslate('my_own_packaging_body'),
      )}
      {renderDropOffTypeCell(
        PackagingTypes.ReusablePackage,
        <Icon name={ReusableBox24} />,
        packagingTypeTranslate('free_reusable_packaging'),
        reusablePackageDimensions &&
          packagingTypeTranslate('free_reusable_packaging_body', {
            dimensions: reusablePackageDimensions,
          }),
      )}
    </>
  )

  const renderDropOff = () =>
    isTypeMailbox(labelOptionsRewrite) ? DropOffType.Mailbox : DropOffType.Home

  const renderDropOffTypeSelectionMethods = () => {
    const dropOffPointLink = {
      'nearest-dropoff-points': (chunks: Array<ReactNode>) => (
        <button key="nearest-dropoff-points" onClick={onShowDropOffPointDialog} type="button">
          <Text as="span" underline text={chunks} clickable />
        </button>
      ),
    }

    return (
      <>
        {renderDropOffTypeCell(
          renderDropOff(),
          <Icon name={Home24} />,
          dropOffTypeTranslate('from_your_address'),
          isTypeMailbox(labelOptionsRewrite)
            ? dropOffTypeTranslate('from_your_address_body')
            : dropOffTypeTranslate('from_home_body'),
        )}
        {renderDropOffTypeCell(
          DropOffType.Point,
          <Icon name={LocationPin24} />,
          dropOffTypeTranslate('from_a_drop_off_point'),
          isDropOffMapEnabled(labelOptionsRewrite)
            ? dropOffTypeTranslate('from_a_drop_off_point_body_link', dropOffPointLink)
            : dropOffTypeTranslate('from_a_drop_off_point_body'),
        )}
      </>
    )
  }

  const renderBodyText = () =>
    isReusablePackagingEnabled ? packagingTypeTranslate('body') : dropOffTypeTranslate('body')

  const renderDropOffTypeSelection = () =>
    labelOptions?.dropOffTypes ? (
      <>
        <Cell body={<Text as="h1" type="heading" text={renderBodyText()} />} />
        {labelOptions.dropOffTypes.map(renderLabelOptionsDropOffTypeMethod)}
      </>
    ) : (
      labelOptionsRewrite && (
        <>
          <Cell body={<Text as="h1" type="heading" text={renderBodyText()} />} />
          {isReusablePackagingEnabled
            ? renderReusableDropOffTypeMethods()
            : renderDropOffTypeSelectionMethods()}
        </>
      )
    )

  const renderModalContent = () => {
    if (labelOptionsUiState === UiState.Pending)
      return <ContentLoader testId="drop-off-selection-modal-loader" />

    return (
      <>
        {renderCarrier()}
        {renderDropOffTypeSelection()}
      </>
    )
  }

  const handleClose = () => {
    clearDropOffTypeSelection()
    clearLabelTypeSelection()
    onClose()
  }

  const handleLabelOptionsPreviousClick = () => {
    clearDropOffTypeSelection()
    if (isReturnShipment(labelOptionsRewrite)) {
      setActiveModal(GenerateLabelModal.ReturnTheOrder)
    } else {
      setActiveModal(GenerateLabelModal.DigitalLabelTypeSelectionModal)
    }
  }

  return (
    <Dialog show={isOpen}>
      <ScrollableArea>
        <Navigation
          body={dropOffTypeTranslate('title')}
          left={
            (isReturnShipment(labelOptionsRewrite) ||
              isPackageTypeLabelTypeFlow(labelOptionsRewrite)) && (
              <Button
                styling="flat"
                iconName={ArrowLeft24}
                inline
                onClick={handleLabelOptionsPreviousClick}
                testId="selection-modal-previous"
                aria={{ 'aria-label': a11yTranslate('back') }}
              />
            )
          }
          right={
            <Button
              styling="flat"
              onClick={handleClose}
              iconName={X24}
              testId="close-selection-modal"
              aria={{ 'aria-label': a11yTranslate('close') }}
              inline
            />
          }
        />
        <Divider />
        {renderModalContent()}
        <div className="u-margin-top-auto">
          <Cell>
            <Button
              styling="filled"
              text={dropOffTypeTranslate('actions.continue')}
              disabled={!isSubmitEnabled}
              onClick={handleSubmit}
              testId="drop-off-type-selection-modal-submit"
            />
          </Cell>
        </div>
      </ScrollableArea>
    </Dialog>
  )
}

export default DropOffTypeSelectionModal
